import { createContext, useContext, useEffect, useState } from 'react';

// Create Theme Context
const ThemeContext = createContext();

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('kids-investment-theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // Fall back to system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove any existing theme classes
    root.removeAttribute('data-theme');
    root.classList.remove('light', 'dark');
    
    // Apply new theme
    root.setAttribute('data-theme', theme);
    root.classList.add(theme);
    
    // Save to localStorage
    localStorage.setItem('kids-investment-theme', theme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#1e293b' : '#ffffff');
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('kids-investment-theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const isDark = theme === 'dark';

  // Theme-aware colors for Chart.js. `dark` can be overridden by surfaces
  // that are always dark (the Gideon chat) regardless of the app theme.
  const getThemeColors = (dark = isDark) => {
    if (dark) {
      return {
        primary: '#60a5fa',     // Light Blue
        success: '#34d399',     // Light Emerald
        warning: '#fbbf24',     // Light Amber
        danger: '#f87171',      // Light Red
        info: '#22d3ee',        // Light Cyan
        purple: '#a78bfa',      // Light Violet
        pink: '#f472b6',        // Light Pink
        orange: '#fb923c',      // Light Orange
        lime: '#a3e635',        // Light Lime
        indigo: '#818cf8',      // Light Indigo
        
        // Chart background colors
        background: '#334155',
        surface: '#1e293b',
        text: '#cbd5e1',
        textSecondary: '#94a3b8',
        border: 'rgba(51, 65, 85, 0.6)',
        grid: 'rgba(255, 255, 255, 0.08)',
        tooltip: 'rgba(15, 23, 42, 0.95)'
      };
    } else {
      return {
        primary: '#3b82f6',     // Blue
        success: '#10b981',     // Emerald
        warning: '#f59e0b',     // Amber
        danger: '#ef4444',      // Red
        info: '#06b6d4',        // Cyan
        purple: '#8b5cf6',      // Violet
        pink: '#ec4899',        // Pink
        orange: '#f97316',      // Orange
        lime: '#84cc16',        // Lime
        indigo: '#6366f1',      // Indigo
        
        // Chart background colors
        background: '#ffffff',
        surface: '#f8fafc',
        text: '#6b7280',
        textSecondary: '#9ca3af',
        border: 'rgba(226, 232, 240, 0.8)',
        grid: 'rgba(0, 0, 0, 0.05)',
        tooltip: 'rgba(0, 0, 0, 0.9)'
      };
    }
  };

  // Get chart color palette
  const getChartColorPalette = (dark = isDark) => {
    const colors = getThemeColors(dark);
    return [
      colors.primary,
      colors.success,
      colors.warning,
      colors.danger,
      colors.purple,
      colors.info,
      colors.lime,
      colors.orange,
      colors.pink,
      colors.indigo
    ];
  };

  const value = {
    theme,
    isDark,
    toggleTheme,
    getThemeColors,
    getChartColorPalette
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Toggle Button Component
export const ThemeToggleButton = ({ className = '', size = 'default' }) => {
  const { theme, toggleTheme } = useTheme();
  
  const sizeClasses = {
    small: 'w-8 h-8 text-sm',
    default: 'w-10 h-10 text-lg',
    large: 'w-12 h-12 text-xl'
  };

  return (
    <button
      onClick={toggleTheme}
      className={`theme-toggle-btn ${sizeClasses[size]} ${className}`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? '🌙' : '☀️'}
    </button>
  );
};

export default useTheme;